import type { SimilarityScoreBreakdown } from "./types.ts";

export const MARKET_COMPARABLE_MAX = 9;
export const MARKET_COMPARABLE_CONFIDENCE_TARGET = 3;

// Hard eligibility is enforced before this policy. These thresholds keep very sparse
// rows out while allowing a strong area/location/condition match from partial XLSX data.
export const MARKET_QUALIFICATION_POLICY = {
  minimumSimilarityScore: 70,
  minimumDataConfidence: 35,
  minimumSelectionScore: 68,
  minimumAreaScore: 0.5,
  minimumConfirmedDimensions: 3
} as const;

export type MarketQualification = {
  qualified: boolean;
  reasons: string[];
  confirmedDimensions: number;
};

export const qualifyMarketComparable = (breakdown: SimilarityScoreBreakdown): MarketQualification => {
  const reasons: string[] = [];
  const components = breakdown.components;
  const confirmedDimensions = [
    (components.location.score ?? 0) >= 0.3,
    components.area.available && (components.area.score ?? 0) >= MARKET_QUALIFICATION_POLICY.minimumAreaScore,
    components.technicalCondition.available,
    components.floorAndElevator.available,
    components.equipment.available,
    components.recency.available && (components.recency.score ?? 0) > 0
  ].filter(Boolean).length;

  if (breakdown.finalScore < MARKET_QUALIFICATION_POLICY.minimumSimilarityScore) reasons.push("nízka potvrdená podobnosť");
  if (breakdown.dataConfidence.score < MARKET_QUALIFICATION_POLICY.minimumDataConfidence) reasons.push("nedostatočná dátová dôvera");
  if (breakdown.selectionScore < MARKET_QUALIFICATION_POLICY.minimumSelectionScore) reasons.push("selection score pod prahom kvality");
  if (!components.area.available || (components.area.score ?? 0) < MARKET_QUALIFICATION_POLICY.minimumAreaScore) {
    reasons.push("nedostatočná zhoda výmery");
  }
  if (confirmedDimensions < MARKET_QUALIFICATION_POLICY.minimumConfirmedDimensions) {
    reasons.push("málo potvrdených porovnávacích dimenzií");
  }

  return { qualified: reasons.length === 0, reasons, confirmedDimensions };
};

export const marketLowDataWarning = (selectedCount: number) => selectedCount < MARKET_COMPARABLE_CONFIDENCE_TARGET
  ? "Málo kvalitných porovnaní – výsledok má nižšiu istotu."
  : undefined;
